/**
 * Pure string transforms used by AIMessage to render partial / streaming
 * markdown without leaking syntax to the user.
 *
 * During streaming the agent's text arrives one token at a time. Without
 * intervention the user sees raw markdown — partial code fences,
 * half-typed image URLs — before the full block lands and ReactMarkdown
 * can render it correctly. These helpers strip the trailing incomplete
 * fragments so the user sees a clean prose-only message until the
 * structured bit is whole.
 *
 * Both functions are conservative: they only strip the LAST unclosed
 * fragment. Earlier complete fragments are preserved verbatim.
 */

/**
 * Strip fenced code blocks (and any trailing unclosed fence).
 *
 * Used when the agent emits JSON in ```...``` fences that we don't want
 * the user to see as a code block — for example, structured-output
 * tool args mid-stream. Completed fences are removed entirely; a
 * trailing unclosed fence (even just a single backtick) is stripped
 * from that point to the end.
 *
 * The agent never produces standalone backticks in user-facing prose,
 * so any `\`` signals a fence boundary.
 */
export function stripJsonBlocks(text: string): string {
  // Strip completed fenced blocks
  let result = text.replace(/```[\s\S]*?```/g, "");
  // Strip any trailing unclosed fence (starts at first unmatched backtick)
  result = result.replace(/`[\s\S]*$/, "");
  return result.trim();
}

/**
 * Hide an incomplete trailing markdown image reference during streaming.
 *
 * Markdown image syntax is `![alt](url)`. Tokens stream left-to-right,
 * so the user briefly sees intermediate states like:
 *
 *   ![Modern campus
 *   ![Modern campus]
 *   ![Modern campus](https://images.unsplash.com/photo-15
 *   ![Modern campus](https://images.unsplash.com/photo-1562774053-...)  ← complete
 *
 * ReactMarkdown renders only the final form as an `<img>`. The
 * intermediate forms render as raw markdown text, which is jarring.
 *
 * Strategy: detect a trailing `![` that hasn't reached its closing `)`
 * and strip from that opener to end of string. Once the closing paren
 * arrives, nothing is stripped and ReactMarkdown gets the whole
 * `![alt](url)` to render normally.
 *
 * Multiple images are handled correctly — only the LAST one (if
 * incomplete) is stripped:
 *
 *   "![one](url) text ![two](url"   →   "![one](url) text "
 *   "![one](url) ![two](url2)"      →   unchanged (both complete)
 *
 * Edge cases:
 * - `![alt](url with spaces)` — `[^)]*` matches the whole url content
 *   because `)` is the only terminator we care about. Markdown image
 *   URLs don't legally contain `)` in the typical form, and our agent
 *   emits CDN URLs that never have them.
 * - `![alt with ] inside]` — `[^\]]*` stops at the first `]`. An alt
 *   with literal `]` inside would partially mis-tokenize, but our
 *   agent-generated alts come from photo metadata that doesn't
 *   contain brackets.
 */
export function hideIncompleteImageMarkdown(text: string): string {
  // Matches a trailing image-markdown fragment missing its closing `)`.
  // - `!\[`                   the opener
  // - `[^\]]*`                the alt text (no `]` allowed inside)
  // - `(?:\](?:\([^)]*)?)?`   optionally: `]` (closing alt) and
  //                            optionally `(url-content)` where url-content
  //                            has no `)` (because if it did, we'd be done)
  // - `$`                     must be at end of string to count as trailing
  return text.replace(/!\[[^\]]*(?:\](?:\([^)]*)?)?$/, "");
}

/**
 * Apply all streaming-safe transformations in the standard order.
 *
 * Order matters: strip JSON fences FIRST (their content might
 * coincidentally contain `![` we wouldn't want to interpret as image
 * markdown), then strip incomplete image markdown.
 */
export function sanitizeForStreaming(
  text: string,
  options: { stripJson?: boolean } = {}
): string {
  let result = text;
  if (options.stripJson) result = stripJsonBlocks(result);
  result = hideIncompleteImageMarkdown(result);
  return result;
}
